import React, { useState, useEffect } from 'react';
import { ReferenceInput, AutocompleteInput, DateInput, Form, useTranslate, useGetResourceLabel, useStore, parseQueryFromLocation } from 'react-admin';
import Card from '@mui/material/Card';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { useLocation, useNavigate } from 'react-router-dom';
import { stringify } from 'query-string';
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';

import Breadcrumb from '../common/layout/Breadcrumb';

import { fetchWeeklies } from '../../services/api';

import weeklies from '.';

const userOptionText = choice => choice?.fullname ? choice.fullname : '';

const boxSx = {
    '& .recharts-legend-item': {
        cursor: 'pointer'
    }
};


const WeeklyOccupancyStats = () => {
    const [data, setData] = useState();
    const [lineData, setLineData] = useState();
    const [params, setParams] = useStore('weeklies.statsParams', {});
    const [userId, setUserId] = useState(params?.filter?.user_id);
    const [startDate, setStartDate] = useState(params?.filter?.start_date);
    const [endDate, setEndDate] = useState(params?.filter?.end_date);
    const [loading, setLoading] = useState(true);
    const [activeSeries, setActiveSeries] = useState([]);

    const getResourceLabel = useGetResourceLabel();
    const translate = useTranslate();
    const location = useLocation();
    const navigate = useNavigate();

    const queryFromLocation = parseQueryFromLocation(location);

    useEffect(() => {
        if (loading && Object.keys(queryFromLocation).length > 0) {
            setParams(queryFromLocation);

            if (queryFromLocation.filter?.user_id) {
                setUserId(queryFromLocation.filter.user_id);
            }
            if (queryFromLocation.filter?.start_date) {
                setStartDate(queryFromLocation.filter.start_date);
            }
            if (queryFromLocation.filter?.end_date) {
                setEndDate(queryFromLocation.filter.end_date);
            }

            setLoading(false);
        }
    }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const fetchData = async () => {
            // TODO: create a true endpoint later for these stats
            const filters = {
                user_id: userId,
                start_date: startDate,
                end_date: endDate,
                sort: 'date',
                order: 'DESC',
                page_size: 200
            };
            const response = await fetchWeeklies(filters);

            let newData = Object.values(response?.results?.reduce((acc, item) => {
                if (acc[item.date]) {
                    return { ...acc, [item.date]: { ...acc[item.date], [item.user.fullname]: { value: item?.occupancy_rate, color: item?.user?.color, name: item?.user?.fullname } } };
                }
                else {
                    return { ...acc, [item.date]: { date: item.date, [item.user.fullname]: { value: item?.occupancy_rate, color: item?.user?.color, name: item?.user?.fullname } } };
                }
            }, {}));
            newData = newData?.reverse();
            setData(newData);
            const newLineData = [];
            newData?.forEach((el) => {
                Object.keys(el).filter((item) => item !== 'date').forEach((key) => {
                    const alreadyExists = key !== 'date' && newLineData.some((item) => item.name === key);
                    if (!alreadyExists) {
                        newLineData.push({
                            name: key,
                            color: el[key]?.color
                        });
                    }
                });
            });
            setLineData(newLineData);

            setParams({ filter: filters });
            navigate({ search: `?${stringify({ filter: JSON.stringify(filters) })}` });
        };

        fetchData();
    }, [userId, startDate, endDate]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleChangeUser = (id) => {
        setUserId(id || null);
    };

    const handleChangeStartDate = (e) => {
        setStartDate(e?.target?.value || null);
    };

    const handleChangeEndDate = (e) => {
        setEndDate(e?.target?.value || null);
    };

    const formatDate = (datetime) => {
        const date = new Date(datetime);
        return date.toLocaleString('default', { day: 'numeric', month: 'numeric', year: 'numeric' });
    };

    const handleLegendClick = (dataKey) => {
        if (activeSeries.includes(dataKey)) {
            setActiveSeries(activeSeries.filter(el => el !== dataKey));
        } else {
            setActiveSeries(prev => [...prev, dataKey]);
        }
    };

    const breadcrumbPath = [
        { url: '/weeklies', title: translate('resources.weeklies.name', { smart_count: 2, _: getResourceLabel('weeklies', 2) }), isActive: false, icon: <weeklies.icon /> },
        { url: '/weeklies/stats', title: translate('Stats'), isActive: true }
    ];

    return (
        <>
            <Breadcrumb path={breadcrumbPath} />
            <Card sx={{ padding: '30px', overflow: 'unset' }}>
                <Form defaultValues={{ user_id: userId, start_date: startDate, end_date: endDate }}>
                    <Box display="flex" gap={1}>
                        <ReferenceInput source="user_id" reference="users" sort={{ field: 'id', order: 'ASC' }}>
                            <AutocompleteInput optionText={userOptionText} size="small" label="User" onChange={handleChangeUser} />
                        </ReferenceInput>
                        <DateInput source="start_date" onChange={handleChangeStartDate} />
                        <DateInput source="end_date" onChange={handleChangeEndDate} />
                    </Box>
                </Form>
                <Grid container>
                    <Grid size={{ lg: 9, xs: 12 }}>
                        <Box height="400px" sx={boxSx}>
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={data}>
                                    <XAxis dataKey="date" tickFormatter={formatDate} />
                                    <YAxis />
                                    <Tooltip labelFormatter={formatDate} />
                                    <Legend iconType="circle" onClick={props => handleLegendClick(props.value)} />
                                    {lineData?.map((item) => (
                                        <Line key={item.name} hide={activeSeries.includes(item.name)} type="monotone" dataKey={`${item.name}.value`} name={item.name} stroke={item.color} />
                                    ))}
                                </LineChart>
                            </ResponsiveContainer>
                        </Box>
                    </Grid>
                </Grid>
            </Card>
        </>
    );
};

export default WeeklyOccupancyStats;